import { protectUploadFromUnload } from "./upload-navigation";

export interface UploadSubmissionGate {
  current: boolean;
}

interface RunUploadSubmissionOptions<T> {
  gate: UploadSubmissionGate;
  onPendingChange: (pending: boolean) => void;
  onPreparingChange?: (preparing: boolean) => void;
  action: (onQueued: () => void) => Promise<T>;
}

/**
 * Starts one upload submission synchronously and rejects re-entry until it settles.
 * The pending callback runs before the async action so the UI can acknowledge a
 * drop immediately. Preparation ends once the global queue owns the tasks;
 * the submission and leave warning remain active until that queue settles.
 */
export async function runUploadSubmission<T>({
  gate,
  onPendingChange,
  onPreparingChange,
  action,
}: RunUploadSubmissionOptions<T>): Promise<T | undefined> {
  if (gate.current) {
    return undefined;
  }

  gate.current = true;
  const releaseUnloadProtection = protectUploadFromUnload();
  let preparing = true;
  const onQueued = () => {
    if (!preparing) return;
    preparing = false;
    onPreparingChange?.(false);
  };
  try {
    onPendingChange(true);
    onPreparingChange?.(true);
    return await action(onQueued);
  } finally {
    releaseUnloadProtection();
    gate.current = false;
    onQueued();
    onPendingChange(false);
  }
}
